import { FC } from 'react'
import { GRAY_1000, WHITE } from 'constants/styling/theme'
import { Stack } from '@mui/material'

/** Sizing/stroke props shared by the plain Propstack glyphs */
interface IconProps {
  /** Rendered width/height in px */
  size?: number
  /** Stroke color */
  color?: string
}

/**
 * Chain-link glyph used across the Propstack purpose messaging (banner tile, badge pill, popover).
 * @example <PropstackChainIcon size={11} color={CORAL_600} />
 */
export const PropstackChainIcon: FC<IconProps> = ({ size = 16, color = WHITE }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 12a5 5 0 007.07 0l2.5-2.5a5 5 0 10-7.07-7.07l-1.4 1.4" />
    <path d="M15 12a5 5 0 00-7.07 0L5.43 14.5a5 5 0 107.07 7.07l1.4-1.4" />
  </svg>
)

/**
 * Layers glyph marking the active Propstack-provisioned workspace in the top bar.
 * @example <PropstackLayersIcon size={13} />
 */
export const PropstackLayersIcon: FC<IconProps> = ({ size = 13, color = WHITE }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l9 5-9 5-9-5 9-5z" />
    <path d="M3 13l9 5 9-5" />
  </svg>
)

interface TileProps {
  /** Tile width/height in px (icon and radius scale proportionally) */
  size?: number
  /** Which glyph the tile carries */
  icon?: 'chain' | 'layers'
}

/**
 * Black rounded-square tile carrying a white Propstack glyph — the visual anchor of the
 * Propstack purpose messaging (44px banner, 30px popover header, 22px top-bar mark).
 * @example <PropstackIconTile size={44} />
 */
export const PropstackIconTile: FC<TileProps> = ({ size = 44, icon = 'chain' }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    sx={{ width: `${size / 10}rem`, height: `${size / 10}rem`, borderRadius: `${size / 40}rem`, background: GRAY_1000 }}
  >
    {icon === 'chain' && <PropstackChainIcon size={Math.round(size * 0.47)} />}

    {icon === 'layers' && <PropstackLayersIcon size={Math.round(size * 0.59)} />}
  </Stack>
)
